import { platformById, safeWebUrl } from "@ondit/publishing";
import { Button, TableCell, TableRow } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { Link } from "react-router";
import { useAccess } from "~/core/access/index";
import type { PageDistributionRow } from "~/core/api/distribution";
import { api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { apiError, keys, read } from "~/core/query";
import { PlatformIcon } from "~/modules/analytics/components/platform-identity";
import { TreeNode } from "~/modules/analytics/components/tree";
import { readingPath } from "~/modules/analytics/lib/loop";

export function deliveryState(row: PageDistributionRow): string {
	// A saved URL and a proposed calendar slot are not delivery receipts.
	if (["published", "user_reported", "unknown", "failed", "cancelled", "remote_draft"].includes(row.state))
		return row.state;
	if (row.state === "sending" || row.schedule_state === "running") return "sending";
	if (row.schedule_state === "attention") return "attention";
	if (row.schedule_state === "waiting") return "scheduled";
	// Produced by distribution automation and waiting for somebody to confirm it.
	if (row.kind === "version" && row.state === "review") return "review";
	return ["ready", "filled", "planned"].includes(row.state) ? row.state : "unknown";
}

/**
 * Where one article went, as the third level of the content tree: a row per
 * platform, in the tree's own columns, so a delivery lines up with the
 * article it came from instead of opening a second table inside the first.
 */
export function PageDistributionRows({ entityId, pageId }: { entityId: string; pageId: string }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "page-distribution", pageId],
		queryFn: () => api.pageDistribution(entityId, pageId),
		...read,
		refetchInterval: 10_000,
	});
	const path = `${readingPath(`/w/${entityId}`, "distribution")}?tab=prepare&page=${encodeURIComponent(pageId)}`;
	const history = readingPath(`/w/${entityId}`, "progress");
	const error = apiError(state.error);

	if (!state.data || state.data.rows.length === 0) {
		return (
			<TableRow>
				<TableCell className="wrap" colSpan={5}>
					<TreeNode depth={2} icon={<Send />}>
						<span className="faint">
							{error ? error.localized(locale) : state.data ? t("pages.distributionEmpty") : t("state.loading")}
						</span>
					</TreeNode>
				</TableCell>
				<TableCell>
					{editor && state.data ? (
						<div className="record-actions">
							<Button variant="default" asChild>
								<Link to={path}>{t("pages.distribute")}</Link>
							</Button>
						</div>
					) : null}
				</TableCell>
			</TableRow>
		);
	}

	const { rows, platforms } = state.data;
	return rows.map((row) => {
		const status = deliveryState(row);
		const ownLabel = ["review", "scheduled", "planned", "published", "user_reported", "attention", "unknown"].includes(
			status,
		);
		const platform = platforms.find((p) => p.id === row.platform);
		const name = row.platform_name || platform?.name || platformById(row.platform)?.name || row.platform;
		const when = ["scheduled", "planned"].includes(status) ? row.planned_at : row.updated_at;
		return (
			<TableRow key={`${row.kind}-${row.id}`}>
				<TableCell className="wrap">
					<TreeNode depth={2} icon={<PlatformIcon platform={platform} />}>
						{/* The delivery itself, where there is one to open. */}
						{row.published_url && safeWebUrl(row.published_url) ? (
							<a
								className="drill"
								href={row.published_url}
								target="_blank"
								rel="noreferrer"
								title={t("publishing.platform.result")}
							>
								{name}
							</a>
						) : (
							name
						)}
						<div className="faint">
							{[
								t("pages.revision", { n: row.revision }),
								row.kind === "version" ? t("pages.delivery.version") : null,
								when ? fmt.dateTime(locale, when) : null,
							]
								.filter(Boolean)
								.join(" · ")}
						</div>
					</TreeNode>
				</TableCell>
				<TableCell>
					<span
						className={
							status === "published"
								? "pill pill-success"
								: ["unknown", "failed", "attention"].includes(status)
									? "pill pill-warning"
									: "pill"
						}
					>
						{t(`${ownLabel ? "pages.delivery" : "publishing.platform.state"}.${status}`)}
					</span>
				</TableCell>
				<TableCell colSpan={3} />
				<TableCell>
					<div className="record-actions">
						<Button variant="default" asChild>
							<Link
								to={
									row.kind === "version"
										? history
										: `${path}&platform=${encodeURIComponent(row.platform)}${row.kind === "task" ? `&task=${encodeURIComponent(row.id)}` : ""}`
								}
							>
								{t("pages.distribution")}
							</Link>
						</Button>
					</div>
				</TableCell>
			</TableRow>
		);
	});
}
